import { Skeleton } from '@/shared/ui/skeleton';
import type { FormFieldSize } from '../../helpers/field-size';
import type { SelectOption } from '../helpers/types';
import { useSelect } from '../hooks/useSelect';
import { MultipleSelect } from './MultipleSelect';
import { SingleSelect } from './SingleSelect';

/**
 * Select コンポーネント
 *
 * Radix UI ベースのドロップダウン選択コンポーネント
 * - 単一選択/複数選択対応
 * - 検索機能付き
 * - キーボードナビゲーション対応
 * - アクセシブル
 *
 * @example
 * ```tsx
 * // 単一選択
 * <Select
 *   value={value}
 *   onValueChange={setValue}
 *   options={[
 *     { label: 'オプション1', value: '1' },
 *     { label: 'オプション2', value: '2' },
 *   ]}
 *   placeholder="選択してください"
 * />
 *
 * // 複数選択
 * <Select
 *   value={values}
 *   onValueChange={setValues}
 *   options={options}
 *   multiple
 *   placeholder="選択してください"
 * />
 *
 * // 検索機能付き
 * <Select
 *   value={value}
 *   onValueChange={setValue}
 *   options={options}
 *   showSearch
 *   placeholder="選択してください"
 * />
 * ```
 */
type SelectProps = {
  /** 選択された値（単一選択: string | number、複数選択: (string | number)[]） */
  value?: string | number | (string | number)[] | undefined;
  /** 値変更時のコールバック */
  onValueChange?: (value: string | number | (string | number)[]) => void;
  /** フォーカス離脱時のコールバック */
  onBlur?: () => void;
  /** Select のオプション */
  options: SelectOption[];
  /** プレースホルダー */
  placeholder?: string;
  /** 検索機能を有効にする */
  showSearch?: boolean;
  /** フィールドの最小幅（デフォルト: sm） */
  size?: FormFieldSize;
  /** 無効化フラグ */
  disabled?: boolean;
  /** ルート要素のカスタムクラス名 */
  rootClassName?: string;
  /** トリガーボタンのカスタムクラス名 */
  triggerClassName?: string;
  /** ドロップダウンコンテンツのカスタムクラス名 */
  contentClassName?: string;
  /** ローディング中フラグ */
  isLoading?: boolean;
  /** フィールドのID (label htmlForと紐付け用) */
  id?: string;
  /** クリアボタンを表示するか（値が入力されている場合のみ表示） */
  showAllClear?: boolean;
  /** エラー状態 */
  hasError?: boolean;
} & (
  | {
      /** 複数選択を有効にする */
      multiple?: true;
      /** 固定された下部オプション */
      fixedBottomOptions?: never;
    }
  | {
      multiple?: false;
      /** 固定された下部オプション */
      fixedBottomOptions?: SelectOption[];
    }
);

export const Select = ({
  value,
  onValueChange,
  onBlur,
  options,
  placeholder = '選択してください',
  showSearch = false,
  multiple = false,
  size = 'sm',
  disabled = false,
  rootClassName,
  triggerClassName,
  contentClassName,
  isLoading = false,
  id,
  fixedBottomOptions,
  showAllClear = false,
  hasError = false,
}: SelectProps) => {
  const {
    searchValue,
    multiOpen,
    filteredOptions,
    activeIndex,
    selectedValues,
    selectedOptions,
    toggleValue,
    handleMultiOpenChange,
    selectValue,
    handleSingleValueChange,
    handleSingleOpenChange,
    singleSearchOpen,
    handleSingleSearchOpenChange,
    handleSearchKeyDown,
    setSearchValue,
    handleClear,
  } = useSelect({
    value,
    onValueChange,
    onBlur,
    options,
    fixedBottomOptions,
    showSearch,
    multiple,
  });

  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (multiple) {
    return (
      <MultipleSelect
        filteredOptions={filteredOptions}
        searchValue={searchValue}
        onSearchValueChange={setSearchValue}
        selectedValues={selectedValues}
        selectedOptions={selectedOptions}
        toggleValue={toggleValue}
        multiOpen={multiOpen}
        onMultiOpenChange={handleMultiOpenChange}
        onClear={handleClear}
        showSearch={showSearch}
        size={size}
        disabled={disabled}
        rootClassName={rootClassName}
        triggerClassName={triggerClassName}
        contentClassName={contentClassName}
        id={id}
        showAllClear={showAllClear}
        placeholder={placeholder}
        hasError={hasError}
        activeIndex={activeIndex}
        onSearchKeyDown={handleSearchKeyDown}
      />
    );
  }

  return (
    <SingleSelect
      filteredOptions={filteredOptions}
      searchValue={searchValue}
      onSearchValueChange={setSearchValue}
      selectValue={selectValue}
      onValueChange={handleSingleValueChange}
      onOpenChange={handleSingleOpenChange}
      onClear={handleClear}
      showSearch={showSearch}
      size={size}
      disabled={disabled}
      rootClassName={rootClassName}
      triggerClassName={triggerClassName}
      contentClassName={contentClassName}
      id={id}
      showAllClear={showAllClear}
      placeholder={placeholder}
      fixedBottomOptions={fixedBottomOptions}
      hasError={hasError}
      activeIndex={activeIndex}
      onSearchKeyDown={handleSearchKeyDown}
      searchOpen={singleSearchOpen}
      onSearchOpenChange={handleSingleSearchOpenChange}
      options={options}
    />
  );
};
